// AppShell.tsx — layout das rotas autenticadas (Calm Operations Console).

import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { List as ListIcon, MagnifyingGlass, X } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';

import { AuraLogo } from '@/components/brand';
import { Badge } from '@/components/ui/Badge';
import { CommandPalette, useCommandPalette } from '@/components/ui/CommandPalette';
import { Toaster } from '@/components/ui/Toaster';
import { UserMenu } from '@/components/ui/UserMenu';
import { jitApi } from '@/lib/api';
import { useSession } from '@/lib/auth/useSession';
import { cn } from '@/lib/utils/cn';

// ───────────────────────────────────────────────────────────────────────────
// Nav model
// ───────────────────────────────────────────────────────────────────────────

type NavLeaf = {
  to: string;
  label: string;
  groups?: readonly string[]; // role gate (undefined = todos)
};

type NavSection = {
  title: string;
  groups?: readonly string[];
  items: NavLeaf[];
};

const DAILY_SECTION: NavSection = {
  title: 'Operação diária',
  items: [
    { to: '/', label: 'Painel' },
    { to: '/requests', label: 'Minhas solicitações' },
  ],
};

const APPROVALS_LINK: NavLeaf = {
  to: '/approvals',
  label: 'Fila de aprovação',
  groups: ['Aura-TEAMAdmins'],
};

const OPERATIONS_SECTION: NavSection = {
  title: 'Operações',
  groups: ['Aura-TEAMAdmins', 'Aura-TEAMAuditors'],
  items: [
    { to: '/admin/grants', label: 'Acessos ativos' },
    { to: '/audit', label: 'Logs' },
  ],
};

const ADMIN_SECTION: NavSection = {
  title: 'Administração',
  groups: ['Aura-TEAMAdmins'],
  items: [
    { to: '/admin/clients', label: 'Clientes' },
    { to: '/admin/users', label: 'Usuários' },
    { to: '/admin/breakglass', label: 'BreakGlass' },
  ],
};

const APPROVALS_POLL_MS = 60_000;

function userInAnyGroup(userGroups: string[] | undefined, targets: readonly string[]): boolean {
  return Boolean(userGroups?.some((g) => targets.includes(g)));
}

// ───────────────────────────────────────────────────────────────────────────
// Approvals badge — polling leve listPendingApprovals limit=50
// ───────────────────────────────────────────────────────────────────────────

function usePendingApprovalsCount(enabled: boolean): number | null {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      setCount(null);
      return;
    }
    let cancelled = false;

    const tick = async () => {
      try {
        const res = await jitApi.listPendingApprovals({ limit: 50 });
        if (!cancelled) setCount(res.items.length);
      } catch {
        // silencioso — badge é nice-to-have, não bloqueia nav
        if (!cancelled) setCount(null);
      }
    };

    void tick();
    const id = window.setInterval(() => void tick(), APPROVALS_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [enabled]);

  return count;
}

// ───────────────────────────────────────────────────────────────────────────
// Root
// ───────────────────────────────────────────────────────────────────────────

export function AppShell() {
  return (
    <CommandPalette>
      <AppShellInner />
      <Toaster />
    </CommandPalette>
  );
}

function AppShellInner() {
  const { user, signOut } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const { setOpen: setPaletteOpen } = useCommandPalette();
  const [signingOut, setSigningOut] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = userInAnyGroup(user?.groups, ['Aura-TEAMAdmins']);
  const showApprovals = userInAnyGroup(user?.groups, APPROVALS_LINK.groups ?? []);
  const showOps = userInAnyGroup(user?.groups, OPERATIONS_SECTION.groups ?? []);
  const showAdmin = userInAnyGroup(user?.groups, ADMIN_SECTION.groups ?? []);

  const pendingCount = usePendingApprovalsCount(isAdmin);

  // fecha drawer mobile ao mudar de rota
  useEffect(() => setMobileOpen(false), [location.pathname]);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      navigate('/login', { replace: true });
    } finally {
      setSigningOut(false);
    }
  };

  const sections = buildVisibleSections(showApprovals, showOps, showAdmin);

  return (
    <div className="min-h-dvh bg-canvas text-ink">
      <aside
        className="fixed inset-y-0 left-0 z-20 hidden w-72 flex-col border-r border-border bg-surface/95 px-4 py-5 shadow-soft md:flex"
        aria-label="Console Aura"
      >
        <SidebarBrand />
        <CommandTrigger onOpen={() => setPaletteOpen(true)} />
        <SidebarNav sections={sections} pendingCount={pendingCount} />
        <div className="mt-auto border-t border-border pt-4">
          {user ? <UserMenu user={user} signingOut={signingOut} onSignOut={handleSignOut} /> : null}
        </div>
      </aside>

      <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-border bg-surface/90 px-4 backdrop-blur md:hidden">
        <Link
          to="/"
          className="flex items-center text-ink transition-opacity hover:opacity-80"
          aria-label="Ir para o painel"
        >
          <AuraLogo variant="navy" size="sm" ariaLabel="Aura" />
        </Link>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="inline-flex items-center justify-center rounded-md border border-border bg-surface p-2 text-muted hover:text-ink"
            aria-label="Buscar ou executar (atalho ⌘K)"
          >
            <MagnifyingGlass size={16} weight="bold" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="inline-flex items-center justify-center rounded-md border border-border bg-surface p-2 text-muted hover:text-ink"
            aria-label="Abrir menu de navegação"
            aria-expanded={mobileOpen}
          >
            <ListIcon size={16} weight="bold" aria-hidden="true" />
          </button>
        </div>
      </header>

      {mobileOpen && (
        <MobileNavSheet
          onClose={() => setMobileOpen(false)}
          sections={sections}
          pendingCount={pendingCount}
          user={user}
          signingOut={signingOut}
          onSignOut={handleSignOut}
          onOpenPalette={() => setPaletteOpen(true)}
        />
      )}

      <main className="min-h-dvh md:pl-72" key={location.pathname}>
        <Outlet />
      </main>
    </div>
  );
}

function buildVisibleSections(
  showApprovals: boolean,
  showOps: boolean,
  showAdmin: boolean,
): NavSection[] {
  const dailyItems = showApprovals
    ? [...DAILY_SECTION.items, APPROVALS_LINK]
    : [...DAILY_SECTION.items];

  return [
    { ...DAILY_SECTION, items: dailyItems },
    ...(showOps ? [OPERATIONS_SECTION] : []),
    ...(showAdmin ? [ADMIN_SECTION] : []),
  ];
}

function SidebarBrand() {
  return (
    <Link
      to="/"
      className="flex items-center gap-3 rounded-lg px-2 py-1.5 text-ink transition-opacity hover:opacity-80"
      aria-label="Ir para o painel"
    >
      <AuraLogo variant="navy" size="md" ariaLabel="Aura" />
      <span className="border-l border-border pl-3 text-xs font-medium uppercase tracking-[0.18em] text-muted">
        Operations console
      </span>
    </Link>
  );
}

function CommandTrigger({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'mt-5 flex w-full items-center gap-2 rounded-lg border border-border bg-canvas px-3 py-2.5',
        'text-left text-sm text-muted transition-colors hover:border-ink/20 hover:text-ink',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40',
      )}
      aria-label="Buscar ou executar (atalho ⌘K)"
    >
      <MagnifyingGlass size={15} weight="bold" aria-hidden="true" />
      <span className="flex-1">Buscar ou executar…</span>
      <kbd className="rounded-sm border border-border bg-surface-muted px-1.5 py-0.5 font-mono text-[0.625rem] text-muted">
        ⌘K
      </kbd>
    </button>
  );
}

function SidebarNav({
  sections,
  pendingCount,
}: {
  sections: NavSection[];
  pendingCount: number | null;
}) {
  return (
    <nav className="mt-6 flex flex-1 flex-col gap-5 overflow-y-auto" aria-label="Navegação principal">
      {sections.map((section) => (
        <NavSectionBlock key={section.title} section={section} pendingCount={pendingCount} />
      ))}
    </nav>
  );
}

function NavSectionBlock({
  section,
  pendingCount,
}: {
  section: NavSection;
  pendingCount: number | null;
}) {
  return (
    <section aria-label={section.title}>
      <h2 className="px-2 font-mono text-[0.625rem] font-semibold uppercase tracking-[0.18em] text-muted">
        {section.title}
      </h2>
      <ul className="mt-2 flex flex-col gap-1">
        {section.items.map((link) => (
          <li key={link.to}>
            <SidebarLink
              link={link}
              badge={link.to === '/approvals' ? (pendingCount ?? undefined) : undefined}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

function SidebarLink({ link, badge }: { link: NavLeaf; badge?: number }) {
  return (
    <NavLink
      to={link.to}
      end={link.to === '/'}
      className={({ isActive }) =>
        cn(
          'flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive ? 'bg-surface-muted text-ink' : 'text-ink/70 hover:bg-surface-muted hover:text-ink',
        )
      }
    >
      <span>{link.label}</span>
      {typeof badge === 'number' && badge > 0 && (
        <Badge variant="info" className="px-1.5 py-0 text-[0.625rem] leading-snug">
          {badge}
        </Badge>
      )}
    </NavLink>
  );
}

interface MobileNavSheetProps {
  onClose: () => void;
  sections: NavSection[];
  pendingCount: number | null;
  user: ReturnType<typeof useSession>['user'];
  signingOut: boolean;
  onSignOut: () => Promise<void>;
  onOpenPalette: () => void;
}

function MobileNavSheet({
  onClose,
  sections,
  pendingCount,
  user,
  signingOut,
  onSignOut,
  onOpenPalette,
}: MobileNavSheetProps) {
  return (
    <div
      className="fixed inset-0 z-30 md:hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Menu de navegação"
    >
      <div
        className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="absolute inset-y-0 left-0 flex w-[min(22rem,88vw)] flex-col overflow-y-auto border-r border-border bg-surface p-4 shadow-xl">
        <div className="flex items-center justify-between">
          <SidebarBrand />
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border p-1.5 text-muted hover:text-ink"
            aria-label="Fechar menu"
          >
            <X size={14} weight="bold" aria-hidden="true" />
          </button>
        </div>
        <CommandTrigger
          onOpen={() => {
            onClose();
            onOpenPalette();
          }}
        />
        <SidebarNav sections={sections} pendingCount={pendingCount} />
        {user && (
          <div className="mt-4 border-t border-border pt-3">
            <UserMenu user={user} signingOut={signingOut} onSignOut={onSignOut} />
          </div>
        )}
      </div>
    </div>
  );
}
